import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useState } from 'react';

import { HiOutlineSearch } from 'react-icons/hi';
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import socket from '@/utils/socket';
import { getShop } from '@/utils/auth';
import CardProduct from './CardProduct';
import ListCategory from './ListCategory';
import { showToast } from '@/utils/toast';
import Lucide from '@/components/Base/Lucide';
import { SocketEvent } from '@/enum/socketEvent';
import empty from "@/assets/images/restaurant 1.png";
import { useAppContext } from '@/use-context/Context';
import Notification from '@/components/Base/Notification';
import { CategoriesType, productType, promotionDiscountType } from '@/config';
import { apiGetCategories, apiGetProduct, getPromotionsDiscount, getPromotionsDiscountByID } from '@/services/ServiceApi';

interface ListProductProps {
    tableId: string;
    setIsCate: (value: boolean) => void;
}

export default function ListProduct({ tableId, setIsCate }: ListProductProps) {
    const shop = getShop();
    const { t } = useTranslation();
    const params = new URLSearchParams(window.location.search);
    const { listItems, setListItems, fetchProduct, setAddToCart, loading, setLoading } = useAppContext();

    const [isClick, setIsClick] = useState<boolean>(false);
    const [categoryId, setCategoryId] = useState<string>("");
    const [textSearch, setTextSearch] = useState<string>("");
    const [searchData, setSearchData] = useState<productType[]>([]);
    const [categories, setCategories] = useState<(promotionDiscountType | CategoriesType)[]>([]);

    const categoryIdFromUrl = params.get("categoryId");
    const savedSearchText = localStorage.getItem('textSearch');
    const getIsDiscountSearch = localStorage.getItem("isDiscountSearch");

    const fetchCategoriesWithPromotions = async (businessId: string) => {
        try {
            const categoryRes = await apiGetCategories(businessId);

            const promotionRes = await getPromotionsDiscount({
                businessId,
                isPagination: false,
                status: true,
            });

            const merged = [
                ...(promotionRes?.data || []),
                ...(categoryRes?.data || []),
            ];

            setCategories(merged);
        } catch (error) {
            console.error("Failed to fetch categories or promotions:", error);
        }
    };

    const fetchProductByKeySearch = (id: string, searchText: string) => {
        setLoading(true);

        apiGetProduct(shop ? shop._id : "", searchText, id)
            .then(response => {
                if (response.success) {
                    const result = response.data;
                    setSearchData(result);
                    setTimeout(() => setLoading(false), 500);
                }
            })
            .catch(error => console.error(error))
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchProductDisCountByKeySearch = (id: string, textSearch: string) => {
        if (id) {
            setLoading(true);
            getPromotionsDiscountByID(id, textSearch)
                .then(response => {
                    if (response.success) {
                        const result = response.data;
                        const discount = response.data.discount || 0;

                        const productsWithDiscount = result.eligibleProducts.map((product: productType) => ({
                            ...product,
                            is_discount: true,
                            discountPercent: Number(discount),
                            discountId: result._id,
                        }));

                        setSearchData(productsWithDiscount);

                        setListItems(prev => {
                            const updatedList = [...prev];

                            productsWithDiscount.forEach(prod => {
                                const existingItem = updatedList.find(
                                    item =>
                                        item._id === prod._id &&
                                        item.discountId === prod.discountId
                                );

                                if (existingItem) {
                                    updatedList[updatedList.indexOf(existingItem)] = {
                                        ...prod,
                                        quantity: existingItem.quantity || 0
                                    };
                                } else {
                                    updatedList.push({ ...prod, quantity: 0 });
                                }
                            });

                            return updatedList;
                        });

                        setTimeout(() => setLoading(false), 500);
                    }
                })
                .catch(error => console.error(error))
                .finally(() => {
                    setLoading(false);
                })
        }
    };

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const debounceFetch = (searchText: string, categoryId: string) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            if (getIsDiscountSearch === "true") {
                fetchProductDisCountByKeySearch(categoryId, searchText);
            } else {
                fetchProductByKeySearch(categoryId, searchText);
            }

            localStorage.setItem('textSearch', searchText);
        }, 500);
    };

    useEffect(() => {
        if (shop) {
            fetchCategoriesWithPromotions(shop._id);
        }
    }, [])

    useEffect(() => {
        setIsCate(isClick)
    }, [isClick])

    useEffect(() => {
        if (categoryIdFromUrl) {
            setCategoryId(categoryIdFromUrl);
        }
    }, []);

    const updateAddToCart = (updatedItem: productType) => {
        setAddToCart(prevAddToCart => {
            const existingItem = prevAddToCart.find(item =>
                item._id === updatedItem._id &&
                item.discountPercent === updatedItem.discountPercent
            );

            if (updatedItem.quantity === 0) {
                return prevAddToCart.filter(item =>
                    !(item._id === updatedItem._id &&
                        item.discountPercent === updatedItem.discountPercent)
                );
            }

            if (existingItem) {
                return prevAddToCart.map(item =>
                    (item._id === updatedItem._id &&
                        item.discountPercent === updatedItem.discountPercent)
                        ? { ...item, quantity: updatedItem.quantity }
                        : item
                );
            }

            return [
                {
                    ...updatedItem,
                    isCheck: true
                },
                ...prevAddToCart,
            ];
        });
    };

    const handleIncrease = (id: string, qtyStock: number, qty: number, discountPercent?: number) => {
        const newQty = qty ? qty : 0;
        if ((newQty + 1) <= qtyStock) {
            setListItems(prev => {
                const index = prev.findIndex(item =>
                    item._id === id &&
                    (item.discountPercent ?? null) === (discountPercent ?? null)
                );

                if (index === -1) {
                    showToast("warning-notification-content");
                    return prev;
                }

                const updated = [...prev];
                const item = updated[index];
                const newQty = (isNaN(item.quantity) ? 0 : item.quantity) + 1;

                updated[index] = {
                    ...item,
                    quantity: newQty,
                };

                updateAddToCart({
                    ...updated[index],
                    quantity: newQty,
                    status: 0,
                    tableId: tableId,
                    isWaiter: true,
                });

                // showToastSuccess(`${t("itemAdded")}`);

                return updated;
            });
        } else {
            showToast("failed-add-stock")
        }
    };

    const handleDecrease = (id: string, discountPercent?: number) => {
        setListItems(prev => {
            const index = prev.findIndex(item =>
                item._id === id &&
                (item.discountPercent ?? null) === (discountPercent ?? null)
            );

            if (index === -1) {
                return prev;
            }

            const updated = [...prev];
            const item = updated[index];
            const currentQuantity = isNaN(item.quantity) ? 0 : item.quantity;

            const newQuantity = Math.max(0, currentQuantity - 1);

            updated[index] = {
                ...item,
                quantity: newQuantity
            };

            updateAddToCart({
                ...updated[index],
                quantity: newQuantity,
            });

            return updated;
        });
    };

    useEffect(() => {
        setTextSearch(savedSearchText ? savedSearchText : "");
        if (!categoryIdFromUrl) {
            localStorage.setItem("isDiscountSearch", "false")
        }
        if (getIsDiscountSearch == "true") {
            fetchProductDisCountByKeySearch(categoryIdFromUrl ? categoryIdFromUrl : "", savedSearchText ? savedSearchText : "")
        } else {
            fetchProductByKeySearch(categoryIdFromUrl ? categoryIdFromUrl : "", savedSearchText ? savedSearchText : "");
        }
    }, []);

    useEffect(() => {
        if (!shop) return;

        if (getIsDiscountSearch === "true") {
            fetchProductDisCountByKeySearch(categoryId, textSearch);
        } else {
            fetchProductByKeySearch(categoryId, textSearch);
        }
    }, [categoryId]);

    useEffect(() => {
        const handleUpdate = (data: any) => {
            if (!data) return;
            if (data.success) {
                fetchProduct();
            }
        };

        const events = [
            SocketEvent.WAITER_RECEIVE_ORDER_FROM_CUSTOMER,
            SocketEvent.REJECT_ORDER,
            SocketEvent.ACCEPT_ORDER,
        ];

        events.forEach((event) => {
            socket.on(event, handleUpdate);
        });

        return () => {
            events.forEach((event) => {
                socket.off(event, handleUpdate);
            });
        };
    }, []);

    const handleText = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setTextSearch(value);

        debounceFetch(value, categoryId);
    };

    const handleCategory = () => {
        localStorage.setItem("isDiscountSearch", "false");
    };

    const handleDiscount = (id: string) => {
        localStorage.setItem("isDiscountSearch", "true");
    };

    const allCategories = [{ _id: "", name: "All Category", nameKH: "ប្រភេទទាំងអស់" }, ...categories];

    const matched: productType[] = savedSearchText
        ? listItems.filter((item) => searchData.some((itemFood) => item._id === itemFood._id))
        : listItems;

    const filteredItems = getIsDiscountSearch === "true"
        ? matched.filter(item =>
            searchData.some(data => (data._id === item._id && item.discountPercent))
        )
        : matched.filter(item =>
            item.categories && item.categories.some(category => (category._id === categoryId && !item.discountPercent))
        );

    return (
        <div>
            <div className="h-[100px]">
                <ListCategory initialCategories={allCategories} setCategoryId={setCategoryId} categoryId={categoryId} setIsClick={setIsClick} handleCategory={handleCategory} handleDiscount={handleDiscount} />
                <div className="flex items-center gap-2 h-10 md:pb-4">
                    <div className="relative">
                        <div className='bg-danger'>
                            <HiOutlineSearch
                                className="text-gray-500 absolute top-1/2 transform -translate-y-1/2 left-3"
                                size={20}
                            />
                        </div>
                        <input
                            type="text"
                            value={textSearch}
                            placeholder={t('searchProduct')}
                            className="bg-white text-[#767676] rounded-[10px] min-w-[250px] w-1/5 border-0 right-0 focus:ring-0 pl-12 text-[14px] h-10"
                            onChange={handleText}
                        />
                    </div>
                </div>
            </div>
            {
                loading ?
                    <div className="h-[40vh] flex items-center justify-center">
                        <AiOutlineLoading3Quarters size={32} className="animate-spin inline-block text-[#767676]" />
                    </div>
                    :
                    <div className="overflow-auto pb-4 mt-2 max-h-[calc(100vh-240px)]" >
                        {
                            filteredItems.length > 0 ?
                                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-8 gap-4">
                                    {
                                        filteredItems.map((item: productType, foodIndex: number) => (
                                            <div key={foodIndex} className="flex shadow-lg rounded-2xl bg-gray-100">
                                                <CardProduct key={foodIndex} item={item} handleIncrease={handleIncrease} handleDecrease={handleDecrease} />
                                            </div>
                                        ))
                                    }
                                </div>
                                :
                                <>
                                    {
                                        categoryId &&
                                        <div className="h-[40vh] flex items-center justify-center">
                                            <div className="flex flex-col items-center">
                                                <div className="min-h-[80] min-w-[80] max-w-[80]">
                                                    <img
                                                        src={empty}
                                                        className="rounded-[10px] object-cover h-[80] w-[80px]"
                                                        alt="Asian Food"
                                                    />
                                                </div>
                                                <p className="text-[16px] font-medium text-[#767676] pt-4">{t('noItem')}</p>
                                            </div>
                                        </div>
                                    }
                                </>
                        }

                        {
                            !categoryId && (
                                matched.length > 0 ? (
                                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-8 gap-4">
                                        {
                                            matched.filter(item => !item.is_discount).map((item: productType, itemIndex: number) => (
                                                <div key={itemIndex} className="flex shadow-lg rounded-2xl bg-gray-100">
                                                    <CardProduct item={item} handleIncrease={handleIncrease} handleDecrease={handleDecrease} />
                                                </div>
                                            ))
                                        }
                                    </div>
                                ) : (
                                    <div className="h-[40vh] flex items-center justify-center">
                                        <div className="flex flex-col items-center">
                                            <div className="min-h-[80] min-w-[80] max-w-[80]">
                                                <img
                                                    src={empty}
                                                    className="rounded-[10px] object-cover h-[80] w-[80px]"
                                                    alt="Asian Food"
                                                />
                                            </div>
                                            <p className="text-[16px] font-medium text-[#767676] pt-4">{t('noItem')}</p>
                                        </div>
                                    </div>
                                )
                            )
                        }
                    </div>
            }

            <Notification id="warning-notification-content" className="flex">
                <Lucide icon="XCircle" className="text-warning" />
                <div className="ml-4 mr-4">
                    <div className="font-medium">{t('cannotAddThisProduct')}</div>
                </div>
            </Notification>

            <Notification id="failed-add-stock" className="flex">
                <Lucide icon="XCircle" className="text-danger" />
                <div className="ml-4 mr-4">
                    <div className="font-medium">{t("outOfStock")}</div>
                </div>
            </Notification>
        </div>
    )
}
